import React, { useState } from 'react';
import TablePagination from '@/components/shared/TablePagination';
import './TaskCalendarTable.scss';
import { safeToLowerCase } from '@/utils/stringUtils';

interface TasksCalendarData {
  id: string;
  date: string;
  execution: {
    status: 'PENDING' | 'DELAY' | 'DONE' | 'WARNING';
  };
  task: {
    name: string;
    type: 'PLUMBING' | 'ELECTRICITY' | 'MAINTENANCE' | 'CLEANING';
  };
  unit: string;
  property: {
    name: string;
    type?: 'residential' | 'commercial' | 'mixed';
  };
}

interface TasksCalendarTableProps {
  data: TasksCalendarData[];
  onRowAction?: (id: string, action: string) => void;
}

const TasksCalendarTable: React.FC<TasksCalendarTableProps> = ({
                                                                 data,
                                                                 onRowAction = () => {}
                                                               }) => {
  const baseClass = 'TasksCalendarTable';
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  // Іконка More
  const MoreIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="3" r="1" fill="#637381"/>
      <circle cx="8" cy="8" r="1" fill="#637381"/>
      <circle cx="8" cy="13" r="1" fill="#637381"/>
    </svg>
  );

  const SortIcon = ({ active = false, direction = 'asc' }) => (
    <div className={`${baseClass}__sort-icon`}>
      <div className={`${baseClass}__sort-arrow ${baseClass}__sort-arrow--up ${active && direction === 'asc' ? `${baseClass}__sort-arrow--active` : ''}`} />
      <div className={`${baseClass}__sort-arrow ${baseClass}__sort-arrow--down ${active && direction === 'desc' ? `${baseClass}__sort-arrow--active` : ''}`} />
    </div>
  );

  // Status badge компонент
  const StatusBadge = ({ status }: { status: string }) => {
    const getStatusConfig = (status: string) => {
      const configs = {
        'PENDING': { label: 'PENDING', className: 'pending' },
        'DELAY': { label: 'DELAY', className: 'delay' },
        'DONE': { label: 'DONE', className: 'done' },
        'WARNING': { label: 'WARNING', className: 'warning' }
      };
      return configs[status as keyof typeof configs] || configs.PENDING;
    };

    const config = getStatusConfig(status);

    return (
      <div className={`${baseClass}__status-badge ${baseClass}__status-badge--${config.className}`}>
        {config.label}
      </div>
    );
  };

  // Форматування дати
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Сортування
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Пагінація
  const totalPages = Math.ceil(data.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = data.slice(startIndex, endIndex);

  const tableHeaders = [
    { key: 'date', label: 'DATE', sortable: true, width: '112px' },
    { key: 'execution', label: 'EXECUTION', sortable: true, width: '133px' },
    { key: 'task', label: 'TASK', sortable: true, width: '432px' },
    { key: 'unit', label: 'UNIT', sortable: true, width: '91px' },
    { key: 'property', label: 'PROPERTY', sortable: true, width: '200px' },
    { key: 'actions', label: '', sortable: false, width: '48px' }
  ];

  return (
    <div className={baseClass}>
      {/* Table */}
      <div className={`${baseClass}__table`}>
        {/* Header */}
        <div className={`${baseClass}__header`}>
          {tableHeaders.map((header) => (
            <div
              key={header.key}
              className={`${baseClass}__header-cell`}
              style={{ width: header.width }}
              onClick={header.sortable ? () => handleSort(header.key) : undefined}
            >
              {header.label && (
                <>
                  <span className={`${baseClass}__header-label`}>{header.label}</span>
                  {header.sortable && (
                    <SortIcon
                      active={sortConfig?.key === header.key}
                      direction={sortConfig?.direction}
                    />
                  )}
                </>
              )}
              {header.key === 'actions' && <MoreIcon />}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className={`${baseClass}__body`}>
          {currentData.length > 0 ? (
            currentData.map((row) => (
              <div key={row.id} className={`${baseClass}__row`}>
                {/* Date */}
                <div className={`${baseClass}__cell`} style={{ width: '112px' }}>
                  <span className={`${baseClass}__date`}>{formatDate(row.date)}</span>
                </div>

                {/* Execution Status */}
                <div className={`${baseClass}__cell`} style={{ width: '133px' }}>
                  <StatusBadge status={row.execution.status} />
                </div>

                {/* Task */}
                <div className={`${baseClass}__cell`} style={{ width: '432px' }}>
                  <div className={`${baseClass}__task-info`}>
                    <span className={`${baseClass}__task-name`}>{row.task.name}</span>
                    <button
                      className={`${baseClass}__ticket-btn`}
                      onClick={() => onRowAction(row.id, 'ticket')}
                    >
                      Ticket
                    </button>
                  </div>
                </div>

                {/* Unit */}
                <div className={`${baseClass}__cell`} style={{ width: '91px' }}>
                  <span className={`${baseClass}__unit`}>{row.unit}</span>
                </div>

                {/* Property */}
                <div className={`${baseClass}__cell`} style={{ width: '200px' }}>
                  <div className={`${baseClass}__property-info`}>
                    <div className={`${baseClass}__property-name`}>{row.property.name}</div>
                    <div className={`${baseClass}__property-badge ${baseClass}__property-badge--${safeToLowerCase(row.property.type || '')}`}>
                      {(row.property.type || 'No units').toUpperCase()}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className={`${baseClass}__cell`} style={{ width: '48px' }}>
                  <button
                    className={`${baseClass}__more-btn`}
                    onClick={() => onRowAction(row.id, 'menu')}
                  >
                    <MoreIcon />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className={`${baseClass}__empty-state`}>
              No data available
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      <TablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        totalRows={data.length}
        onPageChange={setCurrentPage}
        onRowsPerPageChange={setRowsPerPage}
      />
    </div>
  );
};

export default TasksCalendarTable;
